import { useState } from "react";
import { Users, Clock, AlertCircle, Maximize2 } from "lucide-react";
import type { EnrichedDayStats } from "@/lib/reviews/day-statistics-enricher";
import type { ShiftDataWithEmployee } from "@/lib/scheduling/unified-shift-validation-service";
import type { DayTemplateShift } from "@/types";
import DayTemplatePreview from "@/components/shifts/shared/DayTemplatePreview";
import { Button } from "@/components/ui/button";
import { DayTemplatePreviewModal } from "./DayTemplatePreviewModal";
import { format } from "date-fns";

interface StaffingSummaryProps {
  stats: EnrichedDayStats;
}

/**
 * Transform ShiftDataWithEmployee[] to DayTemplateShift[] for DayTemplatePreview
 */
function transformShiftsForPreview(shifts: ShiftDataWithEmployee[]): DayTemplateShift[] {
  return shifts.map((shift) => {
    // Extract time portion from ISO timestamp
    const startTime = format(new Date(shift.start_time), 'HH:mm');
    const endTime = format(new Date(shift.end_time), 'HH:mm');

    // Use employee name as title, or "Unassigned Shift" if no employee
    const title = shift.employee?.name || "Unassigned Shift";

    return {
      startTime,
      endTime,
      title,
      employeeId: shift.employee_id || undefined,
      employeeName: shift.employee?.name || undefined,
      status: shift.status, // Pass status for status-based coloring
    };
  });
}

/**
 * Display staffing summary including employee names, counts, and opening hours
 */
export function StaffingSummary({ stats }: StaffingSummaryProps) {
  const { employees, employeeCount, shiftsCount, hasShifts, shifts } = stats;
  const [showExpandedPreview, setShowExpandedPreview] = useState(false);

  // Show "No shifts scheduled" message when there are no shifts
  if (!hasShifts) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Users className="h-4 w-4 flex-shrink-0" />
        <span>No shifts scheduled</span>
      </div>
    );
  }

  // Transform shifts for preview if available
  const previewShifts = shifts && shifts.length > 0 ? transformShiftsForPreview(shifts) : [];

  // Format employee names
  const employeeNames = employees && employees.length > 0
    ? employees.map(e => e.name).join(', ')
    : 'No assigned staff';

  // Staffing status
  const getStaffingStatus = () => {
    if (!stats.openingHoursCovered && stats.coverageGaps && stats.coverageGaps.length > 0) {
      return {
        text: 'Understaffed',
        className: 'text-red-600 dark:text-red-400',
        icon: <AlertCircle className="h-4 w-4" />
      };
    }
    if (stats.allShiftsAssigned && stats.openingHoursCovered) {
      return {
        text: 'Fully staffed',
        className: 'text-green-600 dark:text-green-400',
        icon: null
      };
    }
    if (!stats.allShiftsAssigned) {
      return {
        text: 'Partially staffed',
        className: 'text-orange-600 dark:text-orange-400',
        icon: <AlertCircle className="h-4 w-4" />
      };
    }
    return null;
  };

  const staffingStatus = getStaffingStatus();

  return (
    <div className="space-y-2 text-sm">
      {/* Staff Info */}
      <div className="flex items-start gap-2">
        <Users className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium">
                {employeeCount || 0} {employeeCount === 1 ? 'employee' : 'employees'}
              </span>
              {shiftsCount > 0 && (
                <span className="text-muted-foreground">
                  • {shiftsCount} {shiftsCount === 1 ? 'shift' : 'shifts'}
                </span>
              )}
              {staffingStatus && (
                <span className={`flex items-center gap-1 ${staffingStatus.className}`}>
                  {staffingStatus.icon}
                  <span>• {staffingStatus.text}</span>
                </span>
              )}
            </div>
            {employees && employees.length > 0 && (
              <p className="text-muted-foreground truncate mt-1">
                {employeeNames}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Day Template Preview - Visual timeline of shifts */}
      {previewShifts.length > 0 && (
        <>
          <div className="mt-3">
            <DayTemplatePreview shifts={previewShifts} width={300} />
          </div>

          {/* Expand button */}
          <div className="mt-2 flex justify-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowExpandedPreview(true)}
              className="text-xs h-6"
            >
              <Maximize2 className="h-3 w-3 mr-1" />
              View All Employees
            </Button>
          </div>

          {/* Expanded modal */}
          <DayTemplatePreviewModal
            open={showExpandedPreview}
            onOpenChange={setShowExpandedPreview}
            shifts={previewShifts}
            date={stats.date}
          />
        </>
      )}

      {/* Opening Hours Info */}
      {stats.status === 'closed' && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Clock className="h-4 w-4 flex-shrink-0" />
          <span>Closed all day</span>
        </div>
      )}

      {/* Coverage Gaps */}
      {stats.coverageGaps && stats.coverageGaps.length > 0 && !stats.openingHoursCovered && (
        <div className="flex items-start gap-2 text-orange-600 dark:text-orange-400">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div className="text-xs">
            <div className="font-medium">Coverage gaps:</div>
            <ul className="list-disc list-inside mt-1 space-y-0.5">
              {stats.coverageGaps.map((gap, idx) => (
                <li key={idx}>
                  {gap.start} - {gap.end} ({Math.round(gap.durationMinutes / 60 * 10) / 10}h)
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
